import { useMemo, useRef, useSyncExternalStore } from 'react';
import { TYPE_COLOR_MAP, type CalendarEventDto, type CalendarEventType } from '../../api/calendar';
import { tCalendar, getCalendarLang, subscribeCalendarLang } from '../../lib/calendar-i18n';
import { formatTime } from '../../lib/base-i18n';
import { computeOverlapLayout } from './lib/overlapLayout';
import { useCalendarDnD } from './useCalendarDnD';
import { isDragBlocked } from './dragPolicy';
import { useCalendarCreateDnD } from './useCalendarCreateDnD';
// ★ 1시간 칸의 높이는 «한 곳»에서만 온다 (2026-09-29 사용자 확정 — 48 에서 60 으로).
//   종전에는 이 파일과 `useCalendarDnD.ts` 가 48 을 각자 적어 두어, 한쪽만 고치면 블록이
//   엉뚱한 시각에 놓였다(60 으로 잰 분을 48 로 그리면 1.25배 어긋난다).
//   ⇒ 「몇 곳에 적혀 있나」는 `timeGridMetrics.guard.test.ts` 가 수로 잠근다.
import { HOUR_PX } from './timeGridMetrics';
import { EventCreatorAvatar } from './EventCreatorAvatar';
import { ReservationBadge } from './ReservationBadge';
import { useEventTooltip } from './eventTooltip';
import { longEventFill, isLongEvent, allDaySolidFill } from './lib/longEventFill';
import { useMyUserId, isMineEvent } from './lib/eventOwnership';

/**
 * 시간격자 뷰 공용 (T-cal22/23) — WeekView(7열)·DayView(1열) 공유.
 *
 * - days: 표시할 날짜들(주=7, 일=1). 각 날의 00:00 기준.
 * - 종일/멀티데이 이벤트는 상단 all-day 행, 시간 이벤트는 시간격자에 absolute 배치.
 * - 겹침은 computeOverlapLayout(T-cal43)로 left/width 계산.
 * - occurrence 는 occurrenceAt 으로 구분(반복 이벤트는 같은 id, 다른 occurrenceAt).
 *
 * 인하우스 CSS Grid(외부 라이브러리 미사용, 디자인 토큰 일관). 드래그는 T-cal21 에서 얹음.
 */
const DAY_START_HOUR = 0;
const DAY_END_HOUR = 24;

interface TimeGridViewProps {
  /** ⭐ 2026-10-19 — 하루종일 줄 높이를 맞출 주(헤더의 주). 없으면 종전처럼 내용만큼 자란다. */
  allDayFocus?: { start: Date; end: Date } | null;
  days: Date[];
  events: CalendarEventDto[];
  loading: boolean;
  onEventClick: (e: CalendarEventDto) => void;
  // T-cal21 — 드래그 이동(옵셔널). deltaMinutes 만큼 시각 평행이동.
  onEventMove?: (e: CalendarEventDto, deltaMinutes: number) => void;
  /**
   * 빈 칸을 끌어 «새 일정 만들기»(옵셔널 · 2026-10-01). 안 넘기면 **종전과 100% 같다** —
   * 핸들러도 안 걸리고 미리보기도 안 그린다(회귀 안전).
   * ⚠ 넘기는 시각은 «그 날 00:00 기준 분»을 더한 실제 시각이다(5분 스냅).
   */
  onSlotCreate?: (start: Date, end: Date) => void;
  /**
   * ⭐ 날짜 머리(요일·일)를 누르면 그 날짜를 고른다 — 우측 레일이 그 날을 보여 준다
   *   (옛 가지 52f74393c · 2026-10-13 이식). ⚠ 미지정이면 머리는 종전처럼 눌리지 않는다.
   */
  onDayHeadClick?: (date: Date) => void;
  /** 우측 레일이 보고 있는 날짜 — 그 날짜 머리를 `--selected` 로 강조한다(옵셔널). */
  selectedDate?: Date | null;
}

/** occurrence 안정 키 — 반복은 id+occurrenceAt, 단일은 id. */
export function occurrenceKey(e: CalendarEventDto): string {
  return e.occurrenceAt ? `${e.id}@${e.occurrenceAt}` : e.id;
}

function sameDay(a: Date, b: Date): boolean {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
}

export function TimeGridView({ days, events, loading, onEventClick, onEventMove, onSlotCreate, onDayHeadClick, selectedDate, allDayFocus }: TimeGridViewProps) {
  // 「내 일정은 굵게」 — 판정은 lib/eventOwnership 한 곳(월간 칩과 «같은» 판정을 쓴다).
  const myUserId = useMyUserId();
  useSyncExternalStore(subscribeCalendarLang, getCalendarLang, getCalendarLang);
  /**
   * 칩 툴팁(2026-09-09) — 종일 칩과 시간 블록이 «이것 하나»를 같이 쓴다.
   * ⚠ 정본은 같은 자리에 훅을 «둘» 불렀다(그쪽은 두 칩이 다른 컴포넌트에 있었다).
   *   여기서는 둘이 같은 컴포넌트 안이라 하나로 족하다 — 늘리면 같은 것을 두 번 부르는 셈이다.
   */
  const tooltipOf = useEventTooltip();

  // 드래그 이동 — occurrenceKey → event 역참조 후 onEventMove 호출.
  const eventByKey = useMemo(() => {
    const m = new Map<string, CalendarEventDto>();
    for (const e of events) m.set(occurrenceKey(e), e);
    return m;
  }, [events]);
  /*
   * ⭐ 옆 날짜로 끌어 옮기기 (2026-10-13 · 옛 가지 b0e0ab630 의 주간 갈래).
   *   좌표 → 날짜 열(`data-daycol`). `document.elementFromPoint` 는 setPointerCapture 와 무관하게
   *   «지금 커서 아래»를 읽는다. 열 폭도 함께 재 둔다 — X 미리보기를 그 폭만큼 옮긴다.
   * ⚠ 놓을 때는 `deltaMinutes + dayDelta × 1440` 하나로 넘긴다 ⇒ 받는 쪽(`handleEventMove`)은
   *   «꼴이 그대로»다(반복 일정 막기·범위 묻기·되돌리기가 이미 그 안에 있다).
   */
  const colWidthRef = useRef(0);
  function getDayColAt(clientX: number, clientY: number): number | null {
    /*
     * ⭐ 2026-10-20 — 옆 날짜로 끌 때 원래 날짜와 왔다갔다 깜빡이던 것.
     *   끄는 블록은 transform 으로 «보이기만» 옆 열에 있고 DOM 은 원래 열 안이다 ⇒ 커서 바로 아래가 그 블록이면
     *   closest('[data-daycol]') 가 원래 열을 줘서 dayDelta 가 0 으로 돌아가고, 블록이 비키면 다시 새 열 — 이것이 반복됐다.
     *   ⇒ 커서 아래 요소들 중 «끄는 블록 안»은 건너뛰고 그 밑의 열을 읽는다.
     */
    if (typeof document.elementsFromPoint === 'function') {
      const under = document.elementsFromPoint(clientX, clientY).find((n) => !n.closest('.calendar-timed-event--dragging'));
      return dayColOf(under);
    }
    if (typeof document.elementFromPoint !== 'function') return null;
    return dayColOf(document.elementFromPoint(clientX, clientY));
  }
  function dayColOf(el: Element | null | undefined): number | null {
    const colEl = el instanceof Element ? el.closest<HTMLElement>('[data-daycol]') : null;
    const raw = colEl?.dataset.daycol;
    if (raw == null) return null;
    const w = colEl!.getBoundingClientRect().width;
    if (w > 0) colWidthRef.current = w;
    const n = Number(raw);
    return Number.isFinite(n) ? n : null;
  }
  const dnd = useCalendarDnD({
    onMove: (key, deltaMinutes, dayDelta) => {
      const e = eventByKey.get(key);
      if (e && onEventMove) onEventMove(e, deltaMinutes + (dayDelta ?? 0) * 1440);
    },
    getDayColAt,
  });

  /**
   * 빈 칸 끌어 만들기 (2026-10-01 · 정본 배선 그대로).
   *
   * ⭐ 「옮기기」(useCalendarDnD)와 «상태를 안 나눈다» — 그리고 핸들러가 걸리는 자리도 다르다:
   *   옮기기는 «이벤트 블록», 만들기는 «빈 시간 눈금 칸». 둘은 DOM 형제라 같은 pointerdown 을
   *   두 번 받지 않는다(블록 위에서 시작한 끌기는 만들기로 안 샌다).
   * ⚠ 가로(날짜) 스크롤과는 훅이 «축»으로 가른다 — 8px 넘기 전엔 아무것도 안 하고, 가로가 크면
   *   손을 뗀다(그 판정은 `useCalendarCreateDnD` 머리말 참조).
   */
  const createDnd = useCalendarCreateDnD({
    onCreate: (dayIndex, startMin, endMin) => {
      if (!onSlotCreate) return;
      const day = days[dayIndex];
      if (!day) return;
      const dayStart = new Date(day);
      dayStart.setHours(0, 0, 0, 0);
      onSlotCreate(
        new Date(dayStart.getTime() + startMin * 60000),
        new Date(dayStart.getTime() + endMin * 60000),
      );
    },
  });

  const hours = useMemo(() => {
    const out: number[] = [];
    for (let h = DAY_START_HOUR; h < DAY_END_HOUR; h++) out.push(h);
    return out;
  }, []);

  // 종일/멀티데이 vs 시간 이벤트 분리.
  const { allDayByDay, timedByDay } = useMemo(() => {
    const allDay = new Map<number, CalendarEventDto[]>();
    const timed = new Map<number, CalendarEventDto[]>();
    for (let i = 0; i < days.length; i++) {
      allDay.set(i, []);
      timed.set(i, []);
    }
    for (const e of events) {
      const start = new Date(e.startAt);
      const end = new Date(e.endAt);
      for (let i = 0; i < days.length; i++) {
        const day = days[i]!;
        // 이 날에 걸치는가.
        const dayStart = new Date(day); dayStart.setHours(0, 0, 0, 0);
        const dayEnd = new Date(dayStart); dayEnd.setDate(dayStart.getDate() + 1);
        if (start < dayEnd && end > dayStart) {
          // ⭐ 2026-10-19 — «하루종일로 만든 일정»만 all-day 행(사용자 지시). 종전에는 자정~자정을 다 덮는 날도
          //   all-day 행으로 보내, 3일 이상 이어지는 시간 일정의 «가운데 날»이 하루종일처럼 보였다.
          //   ⇒ 그 날은 아래 시간표에 그 날 전체(0시~24시)를 덮는 블록으로 그린다(아래 timed 배치가 이미 그 날로 잘라 그린다).
          const spansFullDay = !!e.isAllDay;
          if (spansFullDay) allDay.get(i)!.push(e);
          else timed.get(i)!.push(e);
        }
      }
    }
    return { allDayByDay: allDay, timedByDay: timed };
  }, [days, events]);

  /**
   * ⭐ 2026-10-19 — 하루종일 줄 높이를 «헤더의 주»에서 가장 많은 날에 맞춘다(사용자 지시).
   *   종전에는 가로로 미리 그린 날 전부(약 91일)가 한 줄을 같이 써서, 화면 밖 다른 주에 2건인 날이
   *   하나라도 있으면 지금 주에 1건뿐이어도 2건 높이로 남았다.
   * ⚠ 그 주 밖의 날(가로로 넘기는 중 반쯤 걸린 다음 주 등)은 이 높이에서 잘린다 — 헤더가 넘어가면 다시 맞춘다.
   * 안 넘기면(allDayFocus 없음) 종전 그대로(내용만큼 자란다).
   */
  const allDayRows = useMemo(() => {
    if (!allDayFocus) return null;
    const a = new Date(allDayFocus.start); a.setHours(0, 0, 0, 0);
    const b = new Date(allDayFocus.end); b.setHours(23, 59, 59, 999);
    let max = 0;
    days.forEach((d, i) => {
      if (d >= a && d <= b) max = Math.max(max, allDayByDay.get(i)?.length ?? 0);
    });
    return max;
  }, [allDayFocus, days, allDayByDay]);

  return (
    <div
      className="calendar-timegrid"
      aria-busy={loading}
      style={{ ['--tg-cols' as string]: String(days.length) }}
    >
      {/* 헤더 — 날짜 */}
      <div className="calendar-timegrid-header">
        <div className="calendar-timegrid-gutter-head" />
        {days.map((d, i) => {
          const today = new Date(); today.setHours(0, 0, 0, 0);
          const isToday = sameDay(d, today);
          const dow = d.getDay();
          // 미지정이면 둘 다 빈 글자 ⇒ 종전 className 과 같다.
          const isSel = selectedDate != null && sameDay(d, selectedDate);
          const extra = `${onDayHeadClick ? ' calendar-timegrid-dayhead--clickable' : ''}${isSel ? ' calendar-timegrid-dayhead--selected' : ''}`;
          return (
            <div
              key={i}
              className={`calendar-timegrid-dayhead ${isToday ? 'calendar-timegrid-dayhead--today' : ''} ${dow === 0 ? 'calendar-timegrid-dayhead--sun' : dow === 6 ? 'calendar-timegrid-dayhead--sat' : ''}${extra}`}
              {...(onDayHeadClick ? {
                role: 'button',
                tabIndex: 0,
                'aria-pressed': isSel,
                onClick: () => onDayHeadClick(new Date(d)),
                onKeyDown: (ev: React.KeyboardEvent) => {
                  if (ev.key === 'Enter' || ev.key === ' ') { ev.preventDefault(); onDayHeadClick(new Date(d)); }
                },
              } : {})}
            >
              <span className="calendar-timegrid-dayhead-dow">
                {tCalendar(`calendar.month.dayname.${['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'][dow]}`)}
              </span>
              <span className="calendar-timegrid-dayhead-date">{d.getDate()}</span>
            </div>
          );
        })}
      </div>

      {/* all-day 행 */}
      <div
        className={`calendar-timegrid-allday${allDayRows != null ? ' calendar-timegrid-allday--fit' : ''}`}
        style={allDayRows != null ? ({ ['--tg-allday-rows' as string]: String(allDayRows) } as React.CSSProperties) : undefined}
      >
        <div className="calendar-timegrid-gutter-allday">{tCalendar('calendar.timegrid.allday')}</div>
        {days.map((_, i) => (
          <div key={i} className="calendar-timegrid-allday-cell">
            {allDayByDay.get(i)!.map((e) => {
              const meta = TYPE_COLOR_MAP[e.type as CalendarEventType];
              return (
                <button
                  key={occurrenceKey(e)}
                  type="button"
                  className={`calendar-allday-chip${isMineEvent(e, myUserId) ? ' calendar-allday-chip--mine' : ''}`}
                  style={{
                    // 색 언어 통일(2026-09-09) — 바탕을 채우지 않고 왼쪽 유형색 선으로 말한다.
                    // ⭐ 2026-10-14 — 종일 줄은 모두 하루종일/종일에 걸친 일정 ⇒ 유형색으로 채운다(lib/longEventFill.ts).
                    // ⭐ 2026-10-19 — 시간 일정(연한 바탕 + 왼쪽 선 + 시각)과 확실히 가르게 «진하게 꽉 채우고 흰 글자»(사용자 결정).
                    background: allDaySolidFill(meta?.color ?? 'var(--text-muted)'),
                    color: '#fff',
                  }}
                  onClick={() => onEventClick(e)}
                  /* 2026-09-09: 기본 title 툴팁 «대신» 스타일 툴팁 — 남기면 «둘 다» 뜬다. */
                  {...tooltipOf(e)}
                >
                  <EventCreatorAvatar event={e} className="calendar-event-avatar" />
                  {e.title}
                </button>
              );
            })}
          </div>
        ))}
      </div>

      {/* 시간격자 본문 */}
      <div className="calendar-timegrid-body" style={{ height: `${(DAY_END_HOUR - DAY_START_HOUR) * HOUR_PX}px` }}>
        {/* 시간 눈금 */}
        <div className="calendar-timegrid-gutter">
          {hours.map((h) => (
            <div key={h} className="calendar-timegrid-hour-label" style={{ height: `${HOUR_PX}px` }}>
              {String(h).padStart(2, '0')}:00
            </div>
          ))}
        </div>
        {/* 날짜별 column */}
        {days.map((day, i) => {
          const dayStart = new Date(day); dayStart.setHours(0, 0, 0, 0);
          const dayEnd = new Date(dayStart); dayEnd.setDate(dayStart.getDate() + 1);
          const timed = timedByDay.get(i)!;
          // 겹침 레이아웃 입력 — 이 날 범위로 clamp.
          const layoutInput = timed.map((e) => {
            const s = Math.max(new Date(e.startAt).getTime(), dayStart.getTime());
            const en = Math.min(new Date(e.endAt).getTime(), dayEnd.getTime());
            return { id: occurrenceKey(e), start: s, end: en };
          });
          const boxes = computeOverlapLayout(layoutInput);
          const boxByKey = new Map(boxes.map((b) => [b.item.id, b]));

          return (
            <div key={i} className="calendar-timegrid-daycol" data-daycol={i}>
              {hours.map((h) => (
                <div
                  key={h}
                  className="calendar-timegrid-hourline"
                  style={{ height: `${HOUR_PX}px` }}
                  {...(onSlotCreate ? {
                    onPointerDown: (ev: React.PointerEvent) => {
                      // 이 칸의 위끝에서 «그 날 00:00 의 y» 를 되짚는다 — 칸마다 h 시간만큼 아래에 있다.
                      const rect = (ev.currentTarget as HTMLElement).getBoundingClientRect();
                      createDnd.handlers.onPointerDown(ev, i, rect.top - h * HOUR_PX);
                    },
                    onPointerMove: createDnd.handlers.onPointerMove,
                    onPointerUp: createDnd.handlers.onPointerUp,
                  } : {})}
                />
              ))}
              {/* 끌리는 동안 «만들어질 범위» 미리보기(5분 스냅). 이 열에서 끌 때만 그린다. */}
              {onSlotCreate && createDnd.drag?.dayIndex === i && (
                <div
                  className="calendar-timegrid-create-preview"
                  data-testid="timegrid-create-preview"
                  style={{
                    top: `${(createDnd.drag.startMin / 60) * HOUR_PX}px`,
                    height: `${((createDnd.drag.endMin - createDnd.drag.startMin) / 60) * HOUR_PX}px`,
                  }}
                />
              )}
              {timed.map((e) => {
                const box = boxByKey.get(occurrenceKey(e));
                if (!box) return null;
                const s = Math.max(new Date(e.startAt).getTime(), dayStart.getTime());
                const en = Math.min(new Date(e.endAt).getTime(), dayEnd.getTime());
                const topMin = (s - dayStart.getTime()) / 60000;
                const durMin = Math.max(20, (en - s) / 60000); // 최소 높이 보장
                const meta = TYPE_COLOR_MAP[e.type as CalendarEventType];
                const key = occurrenceKey(e);
                /*
                 * ⛔ 2026-10-05 사용자 결정 — **반복 일정은 끌 수 없다**(까닭·측정값은 `dragPolicy.ts`).
                 *   ⚠ 월간은 `draggable` 속성으로 끄는데 여기는 **pointer 사건**이라 끌 자리가 다르다 —
                 *     ⇒ 핸들러를 안 붙이고 **커서도 'pointer' 로** 돌려야 「끌 수 있구나」가 안 보인다.
                 *   ⚠ 미리보기 오프셋도 이 값으로 가른다 — 안 그러면 안 끌리는 칩이 «따라 움직인다».
                 */
                const canDragThis = !!onEventMove && !isDragBlocked(e);
                const dragOffset = canDragThis ? dnd.previewOffsetPx(key) : 0;
                // 옆 날짜로 넘어간 만큼 X 로 — 열 폭은 getDayColAt 이 잰 값(못 쟀으면 0 ⇒ Y 만).
                const dragOffsetX = canDragThis ? dnd.previewDayDelta(key) * colWidthRef.current : 0;
                const draggingThis = dnd.drag?.eventKey === key;
                return (
                  <button
                    key={key}
                    type="button"
                    className={`calendar-timed-event${draggingThis ? ' calendar-timed-event--dragging' : ''}${isMineEvent(e, myUserId) ? ' calendar-timed-event--mine' : ''}`}
                    style={{
                      top: `${(topMin / 60) * HOUR_PX}px`,
                      height: `${(durMin / 60) * HOUR_PX}px`,
                      left: `calc(${box.left * 100}% + 2px)`,
                      width: `calc(${box.width * 100}% - 4px)`,
                      // 색 언어 통일(2026-09-09) — 월간 칩·종일 칩과 «같은» 언어다.
                      // ⭐ 2026-10-14 — 2일 이상에 걸친 시각 일정의 조각도 채운다(하루 안 일정은 종전 그대로).
                      // 2026-10-14 — 주간·일간 시각 블록은 바탕을 «둔다»(사용자 지시 「주간 캘린더는 배경색이 있어야 해」).
                      //   월간만 바탕을 비운다(MonthView). 여러 날 조각은 유형색으로 채운다.
                      background: isLongEvent(e) ? longEventFill(meta?.color ?? 'var(--text-muted)') : 'var(--surface)',
                      borderLeft: `3px solid ${meta?.color ?? 'var(--text-muted)'}`,
                      transform: dragOffsetX
                        ? `translate(${dragOffsetX}px, ${dragOffset}px)`
                        : dragOffset ? `translateY(${dragOffset}px)` : undefined,
                      cursor: canDragThis ? 'grab' : 'pointer',
                    }}
                    {...(canDragThis ? {
                      onPointerDown: (ev: React.PointerEvent) => dnd.handlers.onPointerDown(ev, key),
                      onPointerMove: dnd.handlers.onPointerMove,
                      onPointerUp: dnd.handlers.onPointerUp,
                    } : {})}
                    onClick={() => { if (!dnd.wasMoved()) onEventClick(e); }}
                    /* 2026-09-09: 기본 title 툴팁 «대신» 스타일 툴팁. 지운 title 의 «시각»은
                       칩이 이미 그린다(바로 아래 .calendar-timed-event-time) — 잃는 것이 없다.
                       ⚠ pointer 핸들러와 이름이 겹치지 않는다(mouse/focus vs pointer). */
                    {...tooltipOf(e)}
                  >
                    {/* 전날부터 이어지는 조각은 시각을 적지 않는다 — 그 날 0시에 시작한 일정으로 읽히지 않게.
                        (2026-10-19 처음엔 「이어짐」을 적었다가 사용자 지시로 걷었다.) */}
                    {new Date(e.startAt).getTime() >= dayStart.getTime() && <span className="calendar-timed-event-time">{formatTime(e.startAt)}</span>}
                    <EventCreatorAvatar event={e} className="calendar-event-avatar" />
                    <span className="calendar-timed-event-title"><ReservationBadge event={e} hasChipTooltip />{e.title}</span>
                  </button>
                );
              })}
            </div>
          );
        })}
      </div>
    </div>
  );
}

interface ViewProps {
  cursor: Date;
  events: CalendarEventDto[];
  loading: boolean;
  onEventClick: (e: CalendarEventDto) => void;
  onEventMove?: (e: CalendarEventDto, deltaMinutes: number) => void;
}

/** WeekView (T-cal22) — anchor 가 속한 주의 일~토 7일. */
export function WeekView({ cursor, events, loading, onEventClick, onEventMove }: ViewProps) {
  const days = useMemo(() => {
    const start = new Date(cursor); start.setHours(0, 0, 0, 0);
    start.setDate(start.getDate() - start.getDay()); // 일요일 시작
    return Array.from({ length: 7 }, (_, i) => {
      const d = new Date(start); d.setDate(start.getDate() + i); return d;
    });
  }, [cursor]);
  return <TimeGridView days={days} events={events} loading={loading} onEventClick={onEventClick} onEventMove={onEventMove} />;
}

/** DayView (T-cal23) — anchor 하루. */
export function DayView({ cursor, events, loading, onEventClick, onEventMove }: ViewProps) {
  const days = useMemo(() => {
    const d = new Date(cursor); d.setHours(0, 0, 0, 0); return [d];
  }, [cursor]);
  return <TimeGridView days={days} events={events} loading={loading} onEventClick={onEventClick} onEventMove={onEventMove} />;
}
